/**
 * Tools + Minecraft-style breaking rules.
 * Reference: docs/MINECRAFT-RULES.md (adapted from minecraft.wiki).
 *
 * Breaking time = hardness × factor:
 *   - correct tool: 1.5 / tier speed (wood 2×, stone 4×)
 *   - bare hands (or wrong tool): 1.5 for soft blocks, 5 for blocks that
 *     REQUIRE a tool (stone/ores) — punching stone takes forever.
 * MVP note: blocks always drop (no "no-drop without tool" rule yet),
 * and tools have no durability yet.
 */

import { BlockType } from "../world/block-registry";
import { ItemType } from "./items";
import { BASE_MELEE_DAMAGE } from "../combat/classes";

export enum ToolKind {
  PICKAXE = "pickaxe",
  AXE = "axe",
  SHOVEL = "shovel",
  SWORD = "sword",
}

export type ToolDef = {
  id: number;
  name: string;
  kind: ToolKind;
  /** Breaking speed multiplier on matching blocks. */
  speed: number;
  /** Melee damage (swords; others fall back to base). */
  damage: number;
};

export const TOOL_DEFS: Record<number, ToolDef> = {
  [ItemType.WOODEN_PICKAXE]: { id: ItemType.WOODEN_PICKAXE, name: "Picareta de madeira", kind: ToolKind.PICKAXE, speed: 2, damage: BASE_MELEE_DAMAGE },
  [ItemType.WOODEN_AXE]: { id: ItemType.WOODEN_AXE, name: "Machado de madeira", kind: ToolKind.AXE, speed: 2, damage: BASE_MELEE_DAMAGE + 1 },
  [ItemType.WOODEN_SHOVEL]: { id: ItemType.WOODEN_SHOVEL, name: "Pá de madeira", kind: ToolKind.SHOVEL, speed: 2, damage: BASE_MELEE_DAMAGE },
  [ItemType.WOODEN_SWORD]: { id: ItemType.WOODEN_SWORD, name: "Espada de madeira", kind: ToolKind.SWORD, speed: 1, damage: 6 },
  [ItemType.STONE_PICKAXE]: { id: ItemType.STONE_PICKAXE, name: "Picareta de pedra", kind: ToolKind.PICKAXE, speed: 4, damage: BASE_MELEE_DAMAGE },
  [ItemType.STONE_AXE]: { id: ItemType.STONE_AXE, name: "Machado de pedra", kind: ToolKind.AXE, speed: 4, damage: BASE_MELEE_DAMAGE + 2 },
  [ItemType.STONE_SHOVEL]: { id: ItemType.STONE_SHOVEL, name: "Pá de pedra", kind: ToolKind.SHOVEL, speed: 4, damage: BASE_MELEE_DAMAGE },
  [ItemType.STONE_SWORD]: { id: ItemType.STONE_SWORD, name: "Espada de pedra", kind: ToolKind.SWORD, speed: 1, damage: 8 },
  [ItemType.IRON_PICKAXE]: { id: ItemType.IRON_PICKAXE, name: "Picareta de ferro", kind: ToolKind.PICKAXE, speed: 6, damage: BASE_MELEE_DAMAGE + 1 },
  [ItemType.IRON_AXE]: { id: ItemType.IRON_AXE, name: "Machado de ferro", kind: ToolKind.AXE, speed: 6, damage: BASE_MELEE_DAMAGE + 4 },
  [ItemType.IRON_SHOVEL]: { id: ItemType.IRON_SHOVEL, name: "Pá de ferro", kind: ToolKind.SHOVEL, speed: 6, damage: BASE_MELEE_DAMAGE + 1 },
  [ItemType.IRON_SWORD]: { id: ItemType.IRON_SWORD, name: "Espada de ferro", kind: ToolKind.SWORD, speed: 1, damage: 11 },
  [ItemType.DIAMOND_PICKAXE]: { id: ItemType.DIAMOND_PICKAXE, name: "Picareta de diamante", kind: ToolKind.PICKAXE, speed: 8, damage: BASE_MELEE_DAMAGE + 2 },
  [ItemType.DIAMOND_AXE]: { id: ItemType.DIAMOND_AXE, name: "Machado de diamante", kind: ToolKind.AXE, speed: 8, damage: BASE_MELEE_DAMAGE + 6 },
  [ItemType.DIAMOND_SHOVEL]: { id: ItemType.DIAMOND_SHOVEL, name: "Pá de diamante", kind: ToolKind.SHOVEL, speed: 8, damage: BASE_MELEE_DAMAGE + 2 },
  [ItemType.DIAMOND_SWORD]: { id: ItemType.DIAMOND_SWORD, name: "Espada de diamante", kind: ToolKind.SWORD, speed: 1, damage: 14 },
};

export function isTool(id: number): boolean {
  return TOOL_DEFS[id] !== undefined;
}

/** Hardness values (seconds base) — from minecraft.wiki. */
const HARDNESS: Partial<Record<BlockType, number>> = {
  [BlockType.DIRT]: 0.5,
  [BlockType.GRASS]: 0.6,
  [BlockType.SAND]: 0.5,
  [BlockType.STONE]: 1.5,
  [BlockType.COBBLESTONE]: 2.0,
  [BlockType.WOOD]: 2.0,
  [BlockType.PLANKS]: 2.0,
  [BlockType.GLASS]: 0.3,
  [BlockType.LEAVES]: 0.2,
  [BlockType.COAL_ORE]: 3.0,
  [BlockType.IRON_ORE]: 3.0,
  [BlockType.GOLD_ORE]: 3.0,
  [BlockType.DIAMOND_ORE]: 3.0,
  [BlockType.CRAFTING_TABLE]: 2.5,
  [BlockType.BED]: 0.6,
  [BlockType.CHEST]: 2.5,
  [BlockType.FURNACE]: 3.5,
};

/** Which tool kind speeds up each block. */
const EFFECTIVE_TOOL: Partial<Record<BlockType, ToolKind>> = {
  [BlockType.STONE]: ToolKind.PICKAXE,
  [BlockType.COBBLESTONE]: ToolKind.PICKAXE,
  [BlockType.COAL_ORE]: ToolKind.PICKAXE,
  [BlockType.IRON_ORE]: ToolKind.PICKAXE,
  [BlockType.GOLD_ORE]: ToolKind.PICKAXE,
  [BlockType.DIAMOND_ORE]: ToolKind.PICKAXE,
  [BlockType.WOOD]: ToolKind.AXE,
  [BlockType.PLANKS]: ToolKind.AXE,
  [BlockType.CRAFTING_TABLE]: ToolKind.AXE,
  [BlockType.CHEST]: ToolKind.AXE,
  [BlockType.FURNACE]: ToolKind.PICKAXE,
  [BlockType.DIRT]: ToolKind.SHOVEL,
  [BlockType.GRASS]: ToolKind.SHOVEL,
  [BlockType.SAND]: ToolKind.SHOVEL,
};

/** Blocks that punish bare hands hard (need a pickaxe in Minecraft). */
const REQUIRES_TOOL = new Set<BlockType>([
  BlockType.STONE,
  BlockType.COBBLESTONE,
  BlockType.COAL_ORE,
  BlockType.IRON_ORE,
  BlockType.GOLD_ORE,
  BlockType.DIAMOND_ORE,
  BlockType.FURNACE,
]);

const DEFAULT_HARDNESS = 1.0;

/**
 * Seconds to break `blockId` while holding `heldItemId` (0/none = hand).
 * Punching stone: 1.5 × 5 = 7.5s. Wooden pickaxe: 1.5 × 1.5 / 2 ≈ 1.1s.
 */
export function getBreakSeconds(blockId: BlockType, heldItemId: number): number {
  const hardness = HARDNESS[blockId] ?? DEFAULT_HARDNESS;
  const tool = TOOL_DEFS[heldItemId];
  const effective = EFFECTIVE_TOOL[blockId];

  if (tool && effective && tool.kind === effective) {
    return (hardness * 1.5) / tool.speed;
  }
  if (REQUIRES_TOOL.has(blockId)) {
    return hardness * 5; // wrong tool / bare hands on stone-likes
  }
  return hardness * 1.5;
}

/** Melee damage for the held item (swords hit harder). */
export function getMeleeDamage(heldItemId: number): number {
  return TOOL_DEFS[heldItemId]?.damage ?? BASE_MELEE_DAMAGE;
}
